import classNames from 'classnames';

import { Panel, StatLabel } from './panel';
import { MiniSegmentedControl } from './mini-segmented-control';
import PlayerSearch from '@components/player-search';
import type { Player as FfhPlayer } from '@api-service/players';
import type { RecentlyViewedPlayer } from '@hooks/useRecentlyViewedPlayers';
import {
  formatInteger,
  formatPrice,
  NO_VALUE
} from '@lib/page-utils/player-profile/format';

export const LANDING_POSITIONS = ['All', 'GK', 'DEF', 'MID', 'FWD'] as const;
export type LandingPosition = (typeof LANDING_POSITIONS)[number];

/** Gameweeks the in-form leaderboard looks back over. */
export const IN_FORM_GAMEWEEK_COUNT = 4;

export type InFormPlayer = {
  optaId: string | null;
  displayName: string;
  position: string;
  teamShortName: string;
  price: number | null;
  points: number | null;
};

export type ClubLink = {
  label: string;
  name: string;
  teamId: string;
};

type PlayerProfileLandingProps = {
  onPlayerClick: (player: FfhPlayer) => void;
  onPlayerSelect: (optaId: string) => void;
  onPaywallOpen: () => void;
  mostOwned: InFormPlayer[];
  /** Pre-season there is no form to rank on, so the section stays hidden. */
  shouldShowInForm: boolean;
  inFormPlayers: InFormPlayer[];
  inFormPosition: LandingPosition;
  onInFormPositionChange: (position: LandingPosition) => void;
  isInFormLoading: boolean;
  recentlyViewed: RecentlyViewedPlayer[];
  clubs: ClubLink[];
  selectedClubId: string | null;
  clubSquad: InFormPlayer[];
  isClubSquadLoading: boolean;
  onClubClick: (teamId: string) => void;
};

const CLUB_SQUAD_SKELETON_ROW_COUNT = 9;

const PROFILE_FEATURES = [
  {
    title: 'Form against expectation',
    description:
      'Points next to expected points for every gameweek, so a hot streak is separable from a sustainable one.'
  },
  {
    title: 'The underlying numbers',
    description:
      'Shots, big chances and key passes, each ranked against every other player in the same position.'
  },
  {
    title: 'Defensive contribution',
    description:
      'How often a player clears the threshold that banks two points, and how close they get when they miss.'
  },
  {
    title: 'What is coming up',
    description:
      'The next six fixtures with difficulty, predicted points and expected minutes.'
  }
];

const InFormCard = ({
  player,
  onSelect
}: {
  player: InFormPlayer;
  onSelect: (optaId: string) => void;
}) => {
  const isSelectable = player.optaId !== null;

  return (
    <button
      type="button"
      disabled={!isSelectable}
      onClick={() => isSelectable && onSelect(player.optaId as string)}
      className={classNames(
        'flex flex-col items-start rounded-[14px] border border-neutral-200 bg-white p-4 text-left transition-colors',
        isSelectable
          ? 'hover:border-neutral-300 hover:bg-neutral-100'
          : 'cursor-default'
      )}
    >
      <p className="font-sans text-base font-bold text-neutral-900">
        {player.displayName}
      </p>
      <p className="mt-0.5 text-xs text-neutral-500">
        {player.position} · {player.teamShortName} · {formatPrice(player.price)}
      </p>
      <p className="mt-3 font-sans text-[28px] font-bold tabular-nums leading-none text-neutral-900">
        {formatInteger(player.points)}
      </p>
      <p className="mt-1 text-xs text-neutral-500">
        pts in {IN_FORM_GAMEWEEK_COUNT} GWs
      </p>
    </button>
  );
};

export const PlayerProfileLanding = ({
  onPlayerClick,
  onPlayerSelect,
  onPaywallOpen,
  mostOwned,
  shouldShowInForm,
  inFormPlayers,
  inFormPosition,
  onInFormPositionChange,
  isInFormLoading,
  recentlyViewed,
  clubs,
  selectedClubId,
  clubSquad,
  isClubSquadLoading,
  onClubClick
}: PlayerProfileLandingProps) => (
  <div className="min-h-screen bg-gray-50">
    <div className="border-t-4 border-primary-600 bg-neutral-800">
      <div className="mx-auto max-w-7xl px-4 pt-12 pb-11 sm:px-6 lg:px-12">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary-600">
          Player profiles
        </p>
        <h1 className="mt-3 max-w-3xl font-sans text-4xl font-bold leading-none tracking-[-0.03em] text-white sm:text-5xl lg:text-[52px]">
          Every number behind a player, in one place
        </h1>
        <p className="mt-4 max-w-xl text-base text-neutral-400">
          Form against expected points, the underlying Opta numbers ranked
          against the position, defensive contribution and what is coming up
          next.
        </p>

        <div className="mt-7 max-w-xl">
          <PlayerSearch
            searchId="player-profile-search"
            label="Search for a player"
            onPlayerClick={onPlayerClick}
            hasPaywall={false}
            showPaywall={onPaywallOpen}
          />
        </div>

        {mostOwned.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-500">Most owned:</span>
            {mostOwned.map((player) => (
              <button
                key={player.displayName}
                type="button"
                disabled={player.optaId === null}
                onClick={() =>
                  player.optaId && onPlayerSelect(player.optaId as string)
                }
                className="rounded-full bg-white/10 px-3 py-1.5 text-[13px] text-white hover:bg-white/20 disabled:cursor-default"
              >
                {player.displayName}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>

    <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-12">
      {shouldShowInForm && (
        <section>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-sans text-xl font-bold text-neutral-800">
                In form right now
              </h2>
              <p className="mt-1 text-sm text-neutral-500">
                Most FPL points over the last {IN_FORM_GAMEWEEK_COUNT} gameweeks
              </p>
            </div>
            <MiniSegmentedControl
              ariaLabel="Filter in-form players by position"
              options={LANDING_POSITIONS.map((position) => ({
                value: position,
                label: position
              }))}
              value={inFormPosition}
              onChange={onInFormPositionChange}
            />
          </div>

          {isInFormLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }, (_unused, index) => (
                <div
                  key={index}
                  className="h-[150px] animate-pulse rounded-[14px] border border-neutral-200 bg-white"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {inFormPlayers.map((player) => (
                <InFormCard
                  key={`${player.displayName}-${player.teamShortName}`}
                  player={player}
                  onSelect={onPlayerSelect}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Panel className="p-5 sm:p-6">
          <h2 className="font-sans text-[17px] font-bold text-neutral-800">
            {recentlyViewed.length === 0
              ? 'Popular profiles'
              : 'Recently viewed'}
          </h2>
          {recentlyViewed.length === 0 ? (
            <>
              <p className="mt-1 text-xs text-neutral-500">
                Profiles you open appear here. Until then, the most-owned
                players in the game:
              </p>
              <ul className="mt-2">
                {mostOwned.map((player) => (
                  <li
                    key={`${player.displayName}-${player.teamShortName}`}
                    className="border-b border-neutral-100 last:border-b-0"
                  >
                    <button
                      type="button"
                      disabled={player.optaId === null}
                      onClick={() =>
                        player.optaId && onPlayerSelect(player.optaId)
                      }
                      className="flex w-full items-center justify-between gap-3 py-3 text-left hover:text-primary-700 disabled:cursor-default"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-neutral-800">
                          {player.displayName}
                        </span>
                        <span className="block text-xs text-neutral-500">
                          {player.position} · {player.teamShortName}
                        </span>
                      </span>
                      <span className="text-[13px] font-bold tabular-nums text-neutral-800">
                        {formatPrice(player.price)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <ul className="mt-2">
              {recentlyViewed.map((player) => (
                <li
                  key={player.optaId}
                  className="border-b border-neutral-100 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => onPlayerSelect(player.optaId)}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left hover:text-primary-700"
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-neutral-800">
                        {player.displayName}
                      </span>
                      <span className="block text-xs text-neutral-500">
                        {player.position} · {player.teamShortName}
                      </span>
                    </span>
                    <span className="text-[13px] font-bold tabular-nums text-neutral-800">
                      {player.seasonPoints === null
                        ? NO_VALUE
                        : `${player.seasonPoints} pts`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="p-5 sm:p-6">
          <h2 className="font-sans text-[17px] font-bold text-neutral-800">
            What is in a profile
          </h2>
          <ul className="mt-2">
            {PROFILE_FEATURES.map((feature) => (
              <li
                key={feature.title}
                className="border-b border-neutral-100 py-3 last:border-b-0"
              >
                <p className="text-sm font-semibold text-neutral-800">
                  {feature.title}
                </p>
                <p className="mt-0.5 text-xs text-neutral-500">
                  {feature.description}
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {clubs.length > 0 && (
        <section>
          <StatLabel>Browse by club</StatLabel>
          <div className="mt-3 flex flex-wrap gap-2">
            {clubs.map((club) => {
              const isSelected = club.teamId === selectedClubId;
              return (
                <button
                  key={club.teamId}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onClubClick(club.teamId)}
                  className={classNames(
                    'min-h-[36px] rounded-lg border px-3 py-2 text-[13px]',
                    isSelected
                      ? 'border-primary-600 bg-primary-600 font-bold text-white'
                      : 'border-neutral-200 bg-neutral-100 text-neutral-800 hover:border-neutral-300 hover:bg-white'
                  )}
                >
                  {club.label}
                </button>
              );
            })}
          </div>

          {selectedClubId !== null && (
            <Panel className="mt-4 p-5 sm:p-6">
              <h2 className="font-sans text-[17px] font-bold text-neutral-800">
                {clubs.find((club) => club.teamId === selectedClubId)?.name ??
                  'Squad'}
              </h2>
              <p className="mt-1 text-xs text-neutral-500">
                Most-owned first. Pick a player to open their profile.
              </p>
              {isClubSquadLoading ? (
                <div className="mt-3 grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from(
                    { length: CLUB_SQUAD_SKELETON_ROW_COUNT },
                    (_unused, index) => (
                      <div
                        key={index}
                        className="my-2 h-10 animate-pulse rounded-md bg-neutral-100"
                      />
                    )
                  )}
                </div>
              ) : (
                <ul className="mt-1 grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
                  {clubSquad.map((player) => (
                    <li
                      key={`${player.displayName}-${player.position}`}
                      className="border-b border-neutral-100"
                    >
                      <button
                        type="button"
                        disabled={player.optaId === null}
                        onClick={() =>
                          player.optaId && onPlayerSelect(player.optaId)
                        }
                        className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:text-primary-700 disabled:cursor-default"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-neutral-800">
                            {player.displayName}
                          </span>
                          <span className="block text-xs text-neutral-500">
                            {player.position}
                          </span>
                        </span>
                        <span className="text-[13px] font-bold tabular-nums text-neutral-800">
                          {formatPrice(player.price)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          )}
        </section>
      )}
    </div>
  </div>
);
